import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ComingSoon, ComingSoonUpdatePayload } from '../models/coming-soon.model';

@Injectable({
  providedIn: 'root',
})
export class ComingSoonService {
  private apiService = inject(ApiService);

  /**
   * Get the Coming Soon draft for a marketing order.
   * Returns an empty draft if no data has been saved yet.
   * @param orderId The marketing order ID
   */
  async getComingSoon$(orderId: string): Promise<ComingSoon> {
    const route = `orders/${orderId}/coming-soon`;
    try {
      const data = await firstValueFrom(
        this.apiService.find$<ComingSoon>(route, { version: 'v2' }).pipe(
          map((response) => new ComingSoon(response)),
        ),
      );
      return data;
    } catch (error) {
      throw Error(this.formatError(error, 'Failed to load Coming Soon data'));
    }
  }

  /**
   * Save the complete Coming Soon draft for a marketing order.
   * @param orderId The marketing order ID
   * @param payload The complete Coming Soon data
   */
  async saveComingSoon$(orderId: string, payload: ComingSoonUpdatePayload): Promise<ComingSoon> {
    const route = `orders/${orderId}/coming-soon`;
    try {
      const data = await this.apiService.update<ComingSoon, ComingSoonUpdatePayload>(route, payload, { version: 'v2' });
      return new ComingSoon(data);
    } catch (error) {
      throw Error(this.formatError(error, 'Failed to save Coming Soon data'));
    }
  }

  async startCampaign$(orderId: string): Promise<ComingSoon> {
    const route = `orders/${orderId}/coming-soon/campaign`;
    try {
      const data = await this.apiService.create<ComingSoon, Record<string, never>>(
        route,
        {},
        { version: 'v2' },
      );
      return new ComingSoon(data);
    } catch (error) {
      throw Error(this.formatError(error, 'Failed to start Coming Soon campaign'));
    }
  }

  private formatError(error: any, defaultMessage: string): string {
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.message) {
      return error.message;
    }
    return defaultMessage;
  }
}
